import { type ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { DateField, Show, useRecordContext } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { NotAuthorized, RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess, type AccessibleRegion } from '../auth/permissions';
import type { Market } from '../dataProvider';
import { formatMoney } from '../cashbacks/formatMoney';
import { TransitionButton } from '../actions/TransitionButton';
import { JustifiedAction } from '../actions/JustifiedAction';
import {
  DestinationCell,
  MemberCell,
  SlaCell,
  StatusChip,
} from './CashoutList';

/**
 * Cashout detail (Show) — where the admin makes the accountable payout DECISIONS
 * (D-cashouts-manual-execution, the web repo's ADR 0005). Execution is MANUAL at
 * launch: the admin approves a `requested` cashout to `processing`, then a HUMAN
 * operator sends the money by hand (UK Faster Payments from the business bank
 * account, US PayPal send) and marks the request `paid` or `failed` here. The
 * automated PayoutEngine is deferred to the cross-repo cashblack-web ADR 0005.
 *
 * So the actions are status-gated: a `requested` row shows Approve / Reject; a
 * `processing` row shows Mark paid / Mark failed; a terminal row shows neither.
 * `approved` is deprecated (no action sets it now, see CashoutStatus).
 *
 * SECURITY: `payoutDetails` (the encrypted envelope) is never materialised onto
 * the cashout row, so it cannot be rendered here — the same absence discipline
 * as the list.
 *
 * Every action is one guarded status write through the provider (audited for free
 * by the #25 wrapper). Approve / Reject need `cashouts:approve`; Mark paid / Mark
 * failed need `cashouts:execute` (super_admin-only, the executing hand). A viewer
 * sees no buttons and any transition is rejected at the chokepoint even if
 * attempted directly.
 */

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

const CashoutShowLayout = () => {
  const record = useRecordContext();
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  if (!record) return null;

  // Record-level region guard — same scoping as the list.
  const inScope = accessibleRegions.includes(
    (record.market as Market).toUpperCase() as AccessibleRegion,
  );
  if (!inScope) return <NotAuthorized />;

  const status = record.status as string;
  const canApprove = hasAccess(
    { role, accessibleRegions },
    { capability: 'cashouts:approve' },
  );
  const canExecute = hasAccess(
    { role, accessibleRegions },
    { capability: 'cashouts:execute' },
  );
  // Legality by rendering (the chokepoint has no transition table): a `requested`
  // row can be approved / rejected; a `processing` row can be marked paid / failed.
  const showApprove = status === 'requested' && canApprove;
  const showExecute = status === 'processing' && canExecute;

  const amount = formatMoney(record.amount, record.currency);

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <MemberCell />
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
              <StatusChip />
              <SlaCell />
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
            }}
          >
            <Field label="Amount">{amount}</Field>
            <Field label="Destination">
              <DestinationCell />
            </Field>
            <Field label="Market">
              {(record.market as string).toUpperCase()}
            </Field>
            <Field label="Requested date">
              <DateField source="requestedAt" showTime />
            </Field>
            {record.paidAt != null && (
              <Field label="Paid date">
                <DateField source="paidAt" showTime />
              </Field>
            )}
          </Box>

          {showApprove && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <TransitionButton
                  resource="cashoutRequests"
                  capability="cashouts:approve"
                  id={record.id}
                  fromStatus={status}
                  toStatus="processing"
                  label="Approve"
                  color="success"
                  confirmTitle="Approve cashout"
                  confirmContent={`Approve this cashout for ${amount}? It moves to processing; a human operator then sends the payment (UK Faster Payments, US PayPal) and marks it paid or failed.`}
                  successMessage="Cashout approved, now processing"
                />
                <TransitionButton
                  resource="cashoutRequests"
                  capability="cashouts:approve"
                  id={record.id}
                  fromStatus={status}
                  toStatus="rejected"
                  label="Reject"
                  color="error"
                  confirmTitle="Reject cashout"
                  confirmContent="Reject this cashout? The member will not be paid."
                  successMessage="Cashout rejected"
                />
              </Stack>
            </>
          )}

          {showExecute && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <TransitionButton
                  resource="cashoutRequests"
                  capability="cashouts:execute"
                  id={record.id}
                  data={{ status: 'paid', paidAt: new Date().toISOString() }}
                  previousData={{ status: 'processing', paidAt: null }}
                  label="Mark paid"
                  color="success"
                  confirmTitle="Mark cashout paid"
                  confirmContent={`Confirm you have sent ${amount} to the member by hand. This records the cashout paid and stamps the paid date.`}
                  successMessage="Cashout marked paid"
                />
                <JustifiedAction
                  resource="cashoutRequests"
                  capability="cashouts:execute"
                  id={record.id}
                  label="Mark failed"
                  color="error"
                  dialogTitle="Mark cashout failed"
                  dialogIntro="Record why the payout failed. The member sees this text in their wallet, so write it for them."
                  buildData={(reason) => ({
                    status: 'failed',
                    failedReason: reason,
                  })}
                  previousData={{ status: 'processing', failedReason: null }}
                  successMessage="Cashout marked failed"
                />
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

/** The detail view. Exported for testing without the access wrapper. */
export const CashoutShowView = () => (
  <Show title="Cashout" actions={false} resource="cashouts">
    <CashoutShowLayout />
  </Show>
);

/** Gated entry point registered as the `cashouts` Resource's show. */
export const CashoutShow = () => (
  <RequireAccess capability="cashouts:read">
    <CashoutShowView />
  </RequireAccess>
);
